import axios from 'axios';
import * as moment from 'moment';
import { memoize } from 'lodash';

import { ICalendarAppText } from '../custom/classes/calendar-text';
import { ICalendarState, ICalendarSettings, IOption } from '../classes/calendar';
import { IEvent } from '../custom/classes/event';
import { IPromoCodeDetail } from '../classes/promo-code';
import { ICalendarFilterRequest } from '../classes/calendar-requests';

export default class CalendarApi {
    private static apiDateFormat = "YYYY-MM-DD";
    private static aceCalendarAPIRoute = process.env.CAL_API_ROUTE;

    private static eventsCache = memoize(CalendarApi.getEventsWrapper, (state: ICalendarState<IEvent>, settings: ICalendarSettings) => `${state.startDate.format('YYYY-MM-DD')}~${state.endDate.format('YYYY-MM-DD')}~${state.productionSeasonFilter.toString()}`);
    private static textCache = memoize(CalendarApi.getTextFromApi, (pageId: number, culture: string) => `${pageId}${culture}`);
    private static filtersCache = memoize(CalendarApi.getFiltersApi, (filter: string, culture: string) => `${filter}${culture}`);

    private static filtersSource = axios.CancelToken.source();

    private static getFiltersApi(filter: string, endDate: string, culture: string): Promise<IOption[]> {
        const startDate = moment().startOf("month").format(this.apiDateFormat);

        var requestString: string = '/';

        if (culture.length > 0) {
            requestString += culture + '/';
        }

        requestString = `${requestString}${this.aceCalendarAPIRoute}${process.env.CAL_FILTER_ROUTE}?name=${filter}&start=${startDate}&end=${endDate}`;

        return axios.get<IOption[]>(requestString, {
            withCredentials: true,
            cancelToken: this.filtersSource.token
        }).then(response => response.data);
    }

    /**
     * Call the API to receive a more specific set of filters.
     * @param filter
     * @param culture
     */
    public static getFiltersFor(filter: string, endDate: moment.Moment, culture = ""): Promise<IOption[]> {
        const endDateFormatted = endDate.format(this.apiDateFormat);

        return this.getFiltersApi(filter, endDateFormatted, culture);

        // This is the memoized variant. Since the request is cancelable, memoize would need to be tweaked to be properly utilized.
        //return this.filtersCache(filter, culture);
    }

    private static getFirstOccurenceForFilterApi(currentFilters: Map<string, string>, endDate: string, culture: string): Promise<string> {
        const startDate = moment().startOf("month").format(this.apiDateFormat);

        var requestString: string = '/';

        if (culture.length > 0) {
            requestString += culture + '/';
        }

        requestString = `${requestString}${this.aceCalendarAPIRoute}${process.env.CAL_FIRST_OCCURRENCE_ROUTE}`;

        //var calendarFilters = [];
        //(Array.from(currentFilters)).forEach((val, key) => {
        //    calendarFilters.push({ filterName: key, filterValue: val });
        //});

        const calendarFilters = (Object as any).entries(currentFilters)
            .map(arr => { return { filterName: arr[0], filterValue: arr[1] } })

        const filterRequest: ICalendarFilterRequest = {
            startDate: startDate,
            endDate: endDate,
            calendarFilters: calendarFilters

        }
        return axios.post(requestString,
            filterRequest,
            {
                method: "POST",
                withCredentials: true,
                cancelToken: this.filtersSource.token
            }
        ).then(response => response.data);
    }

    /**
     * Call the API to receive a more specific set of filters.
     * @param filter
     * @param culture
     */
    public static getFirstOccurenceForFilters(filters: Map<string, string>, endDate: moment.Moment, culture = ""): Promise<string> {
        const endDateFormatted = endDate.format(this.apiDateFormat);

        return this.getFirstOccurenceForFilterApi(filters, endDateFormatted, culture);

        // This is the memoized variant. Since the request is cancelable, memoize would need to be tweaked to be properly utilized.
        //return this.filtersCache(filter, culture);
    }

    /**
     * Invoke this to cancel all outstanding requests to retrieve filters.
     * @param reason
     */
    public static cancelFiltersRequest(reason = "Filters API request manually canceled.") {
        this.filtersSource.cancel(reason);
    }

    /**
     * Wrapper function to automatically determine which API call to make.
     * @param state The calendar state.
     * @param settings The settings of the current calendar.
     */
    private static getEventsWrapper(state: ICalendarState<IEvent>, settings: ICalendarSettings) {
        const startDate = settings.loadWrappingMonthDates ? state.startDate.startOf("week").format(this.apiDateFormat) : state.startDate.format(this.apiDateFormat);
        const endDate = settings.loadWrappingMonthDates ? state.endDate.startOf("week").format(this.apiDateFormat) : state.endDate.format(this.apiDateFormat);

        if (state.productionSeasonFilter > 0) {
            return this.getProductionEventsForDates("", state.productionSeasonFilter, startDate, endDate);
        } else {
            return this.getEventsForDates("", startDate, endDate);
        }
    }

    /**
     * When grabbing all events between two dates from the API.
     * @param culture
     * @param startDate
     * @param endDate
     */
    private static getEventsForDates(culture: string, startDate: string, endDate: string): Promise<IEvent[]> {
        var requestString: string = '/';

        if (culture.length > 0) {
            requestString += culture + '/';
        }

        console.log(this.aceCalendarAPIRoute);
        requestString = requestString + this.aceCalendarAPIRoute + process.env.CAL_ACTION_INDEX + "?startDate=" + startDate + "&endDate=" + endDate;

        return axios.get<IEvent[]>(requestString, {
            withCredentials: true
        }).then(response => response.data);
    }

    /**
     * When grabbing events for a specific production ID. This doesn't seem to be used as frequently anymore.
     * @param culture
     * @param productionSeasonId
     * @param startDate
     * @param endDate
     */
    private static getProductionEventsForDates(culture: string, productionSeasonId: number, startDate: string, endDate: string): Promise<IEvent[]> {
        var requestString: string = '/';

        if (culture.length > 0)
            requestString += culture + '/';
        requestString = requestString + this.aceCalendarAPIRoute + "/" + productionSeasonId.toString() + "/" + "?startDate=" + startDate + "&endDate=" + endDate;

        return axios.get<IEvent[]>(requestString, {
            withCredentials: true
        }).then(response => response.data);
    }

    /**
     * Call this to clear the memoized events and start fresh.
     */
    public static clearEventsCache(): void {
        return this.eventsCache.cache.clear();
    }

    /**
     * The public facing API to retrieve events. Set bustCache to true if you wish to override cached values.
     * @param state The calendar's current state.
     * @param settings The settings utilized by the calendar.
     * @param bustCache Defaults to false, Set to true to force an API call.
     */
    public static getEvents(state: ICalendarState<IEvent>, settings: ICalendarSettings, bustCache = false): Promise<IEvent[]> {
        if (bustCache) {
            this.eventsCache.cache.set(`${state.startDate.format('YYYY-MM-DD')}~${state.endDate.format('YYYY-MM-DD')}~${state.productionSeasonFilter.toString()}`, this.getEventsWrapper(state, settings));
        }

        return this.eventsCache(state, settings);
    }

    /**
     * Grab text data from the API.
     * @param pageId
     * @param culture
     */
    private static getTextFromApi(pageId: number, culture: string): Promise<ICalendarAppText> {
        var requestString: string = '/';

        if (culture.length > 0)
            requestString += culture + '/';
        requestString = `${requestString}${this.aceCalendarAPIRoute}/text?culture=${culture}&pageId=${pageId}`;

        return axios.get<ICalendarAppText>(requestString, {
            withCredentials: true,
        }).then(response => response.data);
    }

    /**
     * Public facing API to obtain the text for a relevant culture on the current page.
     * @param culture
     */
    public static getText(culture: string, bustCache = false): Promise<ICalendarAppText> {
        if (!pageId) {
            var pageId = 0;
        }

        if (bustCache) {
            this.clearTextCache();
        }

        return this.textCache(pageId, culture);
    }

    /**
     * Clear the cache containing the text API information.
     */
    public static clearTextCache() {
        this.textCache.cache.clear();
    }

    /**
     * Makes a POST request to apply a promo code.
     * @param promoCode
     */
    public static applyPromoCode(promoCode: string): Promise<string> {
        var requestString = `/${this.aceCalendarAPIRoute}/ApplyPromoCode`
        return axios.post(requestString,
            {
                promoCode
            },
            {
                method: "POST"
            }
        ).then(response => response.data);
    }

    public static removePromoCode(): Promise<string> {
        var requestString = `/${this.aceCalendarAPIRoute}/RemovePromoCode`
        return axios.post(requestString, {
            method: "POST",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded; charset=utf-8"
            }
        }).then(response => response.data);
    }

    public static getPromoCode(): Promise<IPromoCodeDetail> {
        var requestString = `/${this.aceCalendarAPIRoute}/GetPromoCode`
        return axios.post(requestString, {
            method: "GET",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded; charset=utf-8"
            }
        }).then(response => response.data as IPromoCodeDetail);
    }
}
